import { LINK_QTY_MAX, LINK_QTY_MIN } from "@giftforward/naming";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useState } from "react";
import { commitLinkQtyText, stepLinkQty } from "@/utils/link-quantity-helper";
import { formatUsd } from "@/utils/number-format";

interface Props {
  /** Heading above the stepper — the count means different things per send method, so the caller
   *  supplies the wording ("How many gift links do you need?" vs "How many gifts can be redeemed…"). */
  label: string;
  /** The chosen count (context `linkQuantity`). */
  value: number;
  /** Commit a new count (context `setLinkQuantity`). */
  onChange: (n: number) => void;
  /** Per-gift tier price for the live "× $tier = $total" math; 0 (no tier yet) hides the amount. */
  tier: number;
  /** Optional ⓘ tooltip beside the label (Universal shows one; Manual does not). */
  tooltip?: string;
}

/**
 * Gift-link quantity stepper (− / number / +) with live cost math. Shared by Manual Distribution
 * (Path A — "links to mint") and Universal Link ("redemptions allowed"); the caller supplies the
 * label/tooltip + reads the count off the builder context. Clamp/step rules come from the shared
 * `link-quantity-helper` + `@giftforward/naming` bounds (same source the Add-gift-links wizard uses),
 * so the two never drift.
 */
export default function LinkQuantityStepper({ label, value, onChange, tier, tooltip }: Props) {
  // The stepper edits a text buffer so the field can be cleared mid-typing (an empty box shouldn't
  // snap to 1 on every keystroke); it commits a clamped value on blur. The buffer re-syncs when
  // `value` changes externally (draft hydration), but only when it currently holds a valid number — so
  // a half-typed or empty box is never yanked out from under the user.
  const [qtyText, setQtyText] = useState(() => String(value));
  useEffect(() => {
    // Re-sync the display to `value` ONLY when they diverge on a valid number — i.e. an external change
    // like draft hydration. During live typing onChange keeps the two in lockstep (so this no-ops), an
    // empty/half-typed box parses to NaN (skipped, never yanked away), and an out-of-range entry clamps
    // to the snapped value (desirable). No typing fight, no reset loop.
    const parsed = Number.parseInt(qtyText, 10);
    if (!Number.isNaN(parsed) && parsed !== value) setQtyText(String(value));
  }, [value, qtyText]);

  const commitQty = () => {
    const next = commitLinkQtyText(qtyText);
    onChange(next);
    setQtyText(String(next));
  };
  const step = (delta: 1 | -1) => {
    const next = stepLinkQty(qtyText, delta);
    onChange(next);
    setQtyText(String(next));
  };

  // Route both amounts through the shared USD formatter (GFG-273) — no raw `$` in the UI.
  const mathLabel = tier && value ? `× ${formatUsd(tier)} = ${formatUsd(value * tier)}` : "links";

  return (
    <div>
      <p
        className={`font-lato text-[14px] font-semibold text-rodeo-black mb-2${
          tooltip ? " flex items-center gap-1.5" : ""
        }`}
      >
        {label}
        {tooltip && (
          <Tooltip title={tooltip} enterTouchDelay={0} arrow>
            {/* A focusable button, not a bare icon: MUI icons render aria-hidden, so a keyboard user
                would land on nothing and a screen reader would announce nothing. The button carries
                the accessible name and MUI wires aria-describedby to the tooltip on focus/hover. */}
            <button
              type="button"
              aria-label={tooltip}
              className="inline-flex shrink-0 cursor-help border-0 bg-transparent p-0 text-graphite"
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
            </button>
          </Tooltip>
        )}
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-stretch rounded-btn overflow-hidden border border-smoke bg-white">
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label="Decrease link count"
            className="px-2.5 flex items-center text-graphite hover:bg-clear-dust transition-colors"
          >
            <RemoveIcon sx={{ fontSize: 16 }} />
          </button>
          <input
            type="number"
            min={LINK_QTY_MIN}
            max={LINK_QTY_MAX}
            step={1}
            value={qtyText}
            onChange={(e) => {
              setQtyText(e.target.value);
              const parsed = Number.parseInt(e.target.value, 10);
              if (!Number.isNaN(parsed)) onChange(Math.min(LINK_QTY_MAX, Math.max(0, parsed)));
            }}
            onBlur={commitQty}
            aria-label="Number of gift links"
            className="w-20 text-center font-lato text-[14px] text-rodeo-black outline-none border-x border-smoke [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <button
            type="button"
            onClick={() => step(1)}
            aria-label="Increase link count"
            className="px-2.5 flex items-center text-graphite hover:bg-clear-dust transition-colors"
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </button>
        </div>
        <span className="font-lato text-[14px] text-graphite">{mathLabel}</span>
      </div>
    </div>
  );
}
